/**
 * ダイアログの定型。
 *
 * 破壊度に応じて 2 段階:
 *  - 軽度（再実行・解除など）… `@mail-sv/ui` の `<ConfirmDialog>`
 *  - 重度（ノード削除・ロールバック・組織削除）… `<ConfirmTextDialog>`（対象名の完全一致入力）
 *
 * 送信中はダイアログを閉じられない。閉じられると「押したのに反映されていない」ように見えて、
 * 運営者が同じ操作を二度打つ。
 */
import * as React from 'react';
import { AlertTriangle, Printer } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@mail-sv/ui';
import { errorMessageOf } from './states.js';

/** 印刷物に出すサービス名。他画面（app-layout / login / print-setup-sheet）と同じ取り方。 */
const BRAND_NAME = (import.meta.env.VITE_BRAND_NAME as string | undefined) ?? 'Aurora Host';

/* ---------------------------------------------------------- フォームダイアログ */

export function FormDialog({
  open,
  onOpenChange,
  title,
  description,
  submitLabel = '保存する',
  cancelLabel = 'キャンセル',
  pending = false,
  error,
  disabled = false,
  onSubmit,
  children,
  wide = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  submitLabel?: string;
  cancelLabel?: string;
  pending?: boolean;
  error?: unknown;
  disabled?: boolean;
  onSubmit: () => void;
  children: React.ReactNode;
  wide?: boolean;
}): React.ReactElement {
  return (
    <Dialog
      open={open}
      // 送信中の onOpenChange は握り潰す（ESC / 背景クリックでも閉じない）。
      onOpenChange={(next) => {
        if (pending) return;
        onOpenChange(next);
      }}
    >
      <DialogContent className={wide ? 'max-w-2xl' : undefined} hideClose={pending}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!pending && !disabled) onSubmit();
          }}
        >
          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{errorMessageOf(error)}</AlertDescription>
            </Alert>
          ) : null}

          {children}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
              {cancelLabel}
            </Button>
            <Button type="submit" loading={pending} disabled={disabled}>
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------- 重度の破壊的操作 */

export function ConfirmTextDialog({
  open,
  onOpenChange,
  title,
  description,
  /** ユーザーに一字一句入力させる文字列（通常はホスト名やドメイン名）。 */
  confirmText,
  confirmLabel = '削除する',
  pending = false,
  error,
  onConfirm,
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmText: string;
  confirmLabel?: string;
  pending?: boolean;
  error?: unknown;
  onConfirm: () => void;
  children?: React.ReactNode;
}): React.ReactElement {
  const [typed, setTyped] = React.useState('');
  const inputId = React.useId();
  const matched = typed === confirmText;

  // 開き直したら入力をリセットする。前回の入力が残っていると事故る。
  React.useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (pending) return;
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-md" hideClose={pending}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="size-4" aria-hidden />
            {title}
          </DialogTitle>
          {description ? <DialogDescription>{description}</DialogDescription> : null}
        </DialogHeader>

        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{errorMessageOf(error)}</AlertDescription>
          </Alert>
        ) : null}

        {children}

        <div className="space-y-2">
          <Label htmlFor={inputId}>
            確認のため <code className="font-mono text-foreground">{confirmText}</code> と入力してください
          </Label>
          <Input
            id={inputId}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            spellCheck={false}
            aria-invalid={typed.length > 0 && !matched}
          />
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            キャンセル
          </Button>
          <Button type="button" variant="destructive" onClick={onConfirm} disabled={!matched} loading={pending}>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ------------------------------------------------------ 一度だけ見せる秘密 */

/**
 * 秘密の控えを印刷するための隠しノード。
 *
 * ## なぜ「新しいウィンドウに HTML を書き込む」方式を採らないのか
 *
 * よくある実装は `window.open()` して `document.write()` で HTML を流し込み、
 * `onload` で `print()` を呼ぶ形。**この製品では動かない。**
 * SPA の CSP は `script-src 'self'`（`docker/nginx/spa.conf`）なので、
 * 書き込んだ HTML の中でスクリプトを走らせられない。しかも新しいウィンドウは
 * `about:blank` で CSP を継承するため、`'unsafe-inline'` を足さない限り
 * 印刷を起動できない。**CSP をここで緩めるのは本末転倒**（秘密を扱う画面こそ
 * 一番緩めてはいけない）。
 *
 * そこで**同じページの中に印刷用のノードを持ち**、`@media print` で
 * 「印刷のときだけ、これだけを出す」形にする。`window.print()` は
 * インラインスクリプトではなく React のイベントハンドラから呼ぶので
 * `script-src 'self'` のまま通る。既存の設定情報シート
 * （`routes/print-setup-sheet.tsx`）と同じ作法。
 *
 * ## 印刷物に何を書くか
 *
 * - **値そのもの**（これが目的）。等幅で、折り返さず、行間を空けて読み取りやすく。
 * - **いつ・どのサービスの・誰のものか**。紙は後から出てくるので、
 *   何の紙か分からないと捨ててよいのかも判断できない。
 * - **保管の注意**。この紙自体が「認証を突破できるもの」であることを明記する。
 *
 * QR やバーコードは**載せない**。読み取り機を通す運用は想定しておらず、
 * 「撮影して共有する」経路をわざわざ作ることになる。
 */
function SecretPrintSheet({
  title,
  items,
  issuedAt,
}: {
  title: React.ReactNode;
  items: Array<{ label: string; value: string }>;
  issuedAt: Date;
}): React.ReactElement {
  return (
    <div className="secret-print" aria-hidden>
      <header className="secret-print-head">
        <div>
          <p className="secret-print-title">{title}</p>
          <p className="secret-print-sub">{BRAND_NAME} 運営コンソール</p>
        </div>
        <div className="secret-print-meta">
          <div>発行日時: {issuedAt.toLocaleString('ja-JP')}</div>
        </div>
      </header>

      <p className="secret-print-warn">
        この紙は認証を突破できる情報です。施錠できる場所に保管し、不要になったら細断してください。
        撮影・チャット・メールでの共有はしないでください。
      </p>

      <table className="secret-print-table">
        <tbody>
          {items.map((item) => (
            <tr key={item.label}>
              <th>{item.label}</th>
              <td>
                <code>{item.value}</code>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="secret-print-foot">
        この値は再表示できません。紛失した場合は、認証アプリから登録をやり直してください。
      </p>
    </div>
  );
}

/**
 * 再取得できない値（初期パスワード・登録トークン・招待 URL）の表示。
 * 「閉じたら二度と見られない」ことを本文で明示し、コピーを促す。
 *
 * **印刷（PDF 保存）を 1 クリックで出せる。** リカバリコードのように
 * 「認証アプリを失ったときの最後の手段」は、画面のコピーだけだと
 * 保管し損ねる。ブラウザの印刷ダイアログから PDF として保存できるので、
 * 別途 PDF ライブラリを持ち込まずに済む（`routes/print-setup-sheet.tsx` と同じ手）。
 */
export function SecretRevealDialog({
  open,
  onOpenChange,
  title,
  description,
  items,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  items: Array<{ label: string; value: string }>;
}): React.ReactElement {
  /*
   * **発行日時はダイアログを開いた時刻で固定する。** 毎回 `new Date()` を
   * 評価すると、印刷のたびに（そして再描画のたびに）値が変わる。
   */
  const issuedAt = React.useMemo(() => new Date(), [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            {description ?? 'この値はこの画面を閉じると二度と表示できません。今すぐ控えてください。'}
          </DialogDescription>
        </DialogHeader>

        <Alert variant="warning">
          <AlertDescription>この情報は秘密です。チャットやメールの平文で送らないでください。</AlertDescription>
        </Alert>

        <div className="space-y-3">
          {items.map((item) => (
            <SecretRow key={item.label} label={item.label} value={item.value} />
          ))}
        </div>

        <DialogFooter>
          {/*
            **印刷は「控えました」の左に置く。** 右端の主ボタンは「閉じる」で、
            そこを押すと二度と出せない。先に控える導線を目に入れる。
          */}
          <Button type="button" variant="outline" onClick={() => window.print()}>
            <Printer aria-hidden />
            印刷 / PDF 保存
          </Button>
          <Button type="button" onClick={() => onOpenChange(false)}>
            控えました
          </Button>
        </DialogFooter>

        <SecretPrintSheet title={title} items={items} issuedAt={issuedAt} />
        <SecretPrintStyles />
      </DialogContent>
    </Dialog>
  );
}

/**
 * 印刷用のスタイル。
 *
 * **画面では隠し、印刷のときだけ出す。** そのうえで、印刷時は
 * `body > *` を全部消してこのシートだけを残す。ダイアログはページの
 * 途中に描かれるので、`display: none` を個別に当てて回ると漏れが出る。
 *
 * 紙は常に白地・黒文字にする（運営コンソールのテーマに追随しない）。
 * ダークテーマのまま印刷すると、背景色は既定で出ない一方で文字色だけが
 * 明るいままになり、**ほとんど読めない紙**ができる。
 */
function SecretPrintStyles(): React.ReactElement {
  return (
    <style>{`
.secret-print { display: none; }

@media print {
  /*
   * アプリ本体（#root）は場所ごと消す。ダイアログは body 直下へ描かれるので巻き込まない。
   * visibility だけだと本体が紙の長さぶん場所を取り、fixed のこのシートが白紙の各ページに
   * 繰り返し刷られる（AppShell は印刷時に高さの固定を外すので、長い画面ほど枚数が増える）。
   */
  #root { display: none !important; }
  /* ダイアログの外にあるものを全部消す。 */
  body * { visibility: hidden !important; }
  .secret-print, .secret-print * { visibility: visible !important; }
  .secret-print {
    display: block !important;
    position: fixed;
    inset: 0;
    margin: 0;
    padding: 0;
    background: #fff;
    color: #000;
    font-family: system-ui, -apple-system, "Segoe UI", "Hiragino Kaku Gothic ProN", "Yu Gothic", sans-serif;
    font-size: 10.5pt;
    line-height: 1.6;
  }
  .secret-print-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8mm;
    border-bottom: 1.5pt solid #000;
    padding-bottom: 3mm;
  }
  .secret-print-title { font-size: 15pt; font-weight: 700; margin: 0; }
  .secret-print-sub { font-size: 9pt; margin: 1mm 0 0; }
  .secret-print-meta { font-size: 9pt; text-align: right; white-space: nowrap; }
  .secret-print-warn {
    margin: 4mm 0;
    padding: 3mm;
    border: 1pt solid #000;
    font-size: 9.5pt;
  }
  .secret-print-table { width: 100%; border-collapse: collapse; margin-top: 2mm; }
  .secret-print-table th,
  .secret-print-table td {
    border: 0.5pt solid #000;
    padding: 2.5mm 3mm;
    text-align: left;
    vertical-align: top;
  }
  .secret-print-table th { width: 32mm; font-size: 9.5pt; font-weight: 600; }
  /* **値は折り返す。** 長いトークンが紙の外へはみ出すと控えとして使えない。 */
  .secret-print-table code {
    font-family: ui-monospace, SFMono-Regular, "Consolas", monospace;
    font-size: 11pt;
    letter-spacing: 0.02em;
    word-break: break-all;
    white-space: pre-wrap;
  }
  .secret-print-foot { margin-top: 4mm; font-size: 9pt; }
}
@page { size: A4; margin: 16mm; }
`}</style>
  );
}

function SecretRow({ label, value }: { label: string; value: string }): React.ReactElement {
  const [copied, setCopied] = React.useState(false);
  return (
    <div className="space-y-1">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <div className="flex items-stretch gap-2">
        <code className="min-w-0 flex-1 overflow-x-auto whitespace-pre rounded-md border border-border bg-panel px-3 py-2 font-mono text-xs">
          {value}
        </code>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            void navigator.clipboard.writeText(value).then(() => {
              setCopied(true);
              setTimeout(() => setCopied(false), 1600);
            });
          }}
        >
          {copied ? 'コピー済' : 'コピー'}
        </Button>
      </div>
    </div>
  );
}
